import { useCallback, useMemo, useState } from "react";
import Fuse from "fuse.js";
import {
  accountsPathsArr,
  assetsArray,
  contractArray,
  crmArray,
  hrArray,
  inventoryArray,
  propertyManagementArray,
  registryArray,
  settingsPathArr,
  userPortalArray,
} from "../../../ListOfPaths/ListOfPaths";

const flattenPaths = (paths) => {
  const flat = [];

  paths.forEach((p) => {
    if (p.subbtn) {
      p.subbtn.forEach((sub) => {
        if (sub.subTab) {
          sub.subTab.forEach((tab) => {
            flat.push(tab);
          });
        } else {
          flat.push(sub);
        }
      });
    } else {
      flat.push(p);
    }
  });

  return flat;
};

const selectActiveMenu = (currentUrl, arr) => {
  if (Array.isArray(arr) && arr.length > 0) {
    return arr;
  }
  if (currentUrl.includes("/account")) {
    return accountsPathsArr;
  }
  if (currentUrl.includes("/inventory")) {
    return inventoryArray;
  }
  if (currentUrl.includes("/hr")) {
    return hrArray;
  }
  if (currentUrl.includes("/crm")) {
    return crmArray;
  }
  if (currentUrl.includes("/asset")) {
    return assetsArray;
  }
  if (/^\/settings(?:\/|\?|$)/i.test(currentUrl)) {
    return settingsPathArr;
  }
  if (currentUrl.includes("/user")) {
    return userPortalArray;
  }
  if (currentUrl.includes("/contract") || currentUrl.includes("/sales")) {
    return contractArray;
  }
  if (currentUrl.includes("/property")) {
    return propertyManagementArray;
  }
  if (currentUrl.includes("/registry")) {
    return registryArray;
  }
  return arr || [];
};

const useSidebarSearch = ({ arr, currentUrl }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const activeItems = useMemo(() => {
    const activeMenu = selectActiveMenu(currentUrl, arr);
    return flattenPaths(activeMenu);
  }, [currentUrl, arr]);

  const fuse = useMemo(
    () =>
      new Fuse(activeItems, {
        keys: ["text", "name", "info"],
        threshold: 0.3,
      }),
    [activeItems],
  );

  const clearSearch = useCallback(() => {
    setQuery("");
    setResults([]);
    setHighlightedIndex(-1);
  }, []);

  const handleQueryChange = useCallback(
    (value) => {
      const normalized = value || "";
      setQuery(normalized);

      if (normalized.trim().length > 0) {
        const searchResults = fuse.search(normalized.trim());
        setResults(searchResults.map((result) => result.item));
        setHighlightedIndex(0);
      } else {
        setResults([]);
        setHighlightedIndex(-1);
      }
    },
    [fuse],
  );

  const handleKeyDown = useCallback(
    (event, onSelect) => {
      if (!results.length) {
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((current) => (current + 1) % results.length);
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((current) => (current <= 0 ? results.length - 1 : current - 1));
      }

      if (event.key === "Enter") {
        event.preventDefault();
        if (highlightedIndex >= 0 && results[highlightedIndex]) {
          onSelect(results[highlightedIndex]);
        }
      }
    },
    [highlightedIndex, results],
  );

  return {
    query,
    results,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear: clearSearch,
    handleKeyDown,
  };
};

export default useSidebarSearch;
